// A procedural ocean ambience bed: looping brown-noise "surf" through a slowly-modulated
// low-pass, with a slow swell LFO. No samples / no licensing — generated at runtime. The
// generative `space` axis sets its level so the sea breathes with the soundscape.
import { mulberry32 } from '@shared/rng';

export class OceanBed {
  readonly output: GainNode;
  private readonly ctx: AudioContext;
  private readonly sources: AudioScheduledSourceNode[];

  constructor(ctx: AudioContext) {
    this.ctx = ctx;
    const sr = ctx.sampleRate;

    // ~6s of brown noise, looped.
    const len = Math.floor(sr * 6);
    const buf = ctx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    const rng = mulberry32(0x0cea);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = rng() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    // taper the loop seam to avoid a click
    const fade = Math.floor(sr * 0.05);
    for (let i = 0; i < fade; i++) {
      const g = i / fade;
      data[i] *= g;
      data[len - 1 - i] *= g;
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 380;
    lp.Q.value = 0.6;

    // slow brightness drift on the low-pass (gentle "waves nearer / further")
    const lpLfo = ctx.createOscillator();
    lpLfo.frequency.value = 0.033;
    const lpDepth = ctx.createGain();
    lpDepth.gain.value = 140;
    lpLfo.connect(lpDepth).connect(lp.frequency);

    // swell on amplitude
    const swell = ctx.createGain();
    swell.gain.value = 1;
    const swLfo = ctx.createOscillator();
    swLfo.frequency.value = 0.06;
    const swDepth = ctx.createGain();
    swDepth.gain.value = 0.45;
    swLfo.connect(swDepth).connect(swell.gain);

    this.output = ctx.createGain();
    this.output.gain.value = 0.0001;

    src.connect(lp).connect(swell).connect(this.output);
    src.start();
    lpLfo.start();
    swLfo.start();
    this.sources = [src, lpLfo, swLfo];
  }

  /** space in [0,1] -> bed presence. */
  setLevel(space: number): void {
    const target = 0.04 + 0.07 * Math.max(0, Math.min(1, space));
    this.output.gain.setTargetAtTime(target, this.ctx.currentTime, 1.5);
  }

  /** Stop the looping sources + LFOs and disconnect (teardown path). */
  dispose(): void {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
      s.disconnect();
    }
    this.output.disconnect();
  }
}
